import {
  ButtonType,
  DialogPositioningBehavior,
  DialogType,
  TabStyles,
} from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';

import { DIALOG_WIDTH, dialogBodyStyle } from '../../app/content/styles/ExternalContentActionsStyles';
import { delayPromise } from '../../app/utils/Delay';
import { Messages } from '../../codegen/Localize';
import { ButtonWithSpinner } from '../../common/react/components/ButtonWithSpinner';
import { ModalInstance, ReactModalOptions, ReactModalService } from './ReactModalService';
import { cssClass } from '../../style';


/**
 * Options for a React-based "Confirm action dialog", similar to the old Angular `IConfirmActionDialogOptions`.
 *
 * These options might be incomplete; not all the Angular functionality is implemented in React.
 * If you need more functionality, add it!
 */
export type ReactConfirmActionDialogOptions<P extends Object, S, TResult> = {
  /**
   * The title of the dialog, displayed in its title bar
   * If title is empty then we won't display a dialog title bar.
  */
  readonly title: string;

  /** The main content of the dialog, where the user can configure the state data that gets passed into the action. */
  readonly bodyComponent: React.ComponentType<ConfirmActionDialogBodyProps<P, S>>;

  /** The initial value of the dialog's state data */
  readonly initialDialogState: S;

  /** Additional static information passed to the dialog body component */
  readonly initialBodyProps: P;

  /** The name of the single button that confirms the action. The name might be dependent on the data from the dialog body */
  readonly actionName: string | ((bodyProps: P, dialogState: S) => string);

  /**
   * The action to take when the user clicks the confirmation button.
   * If the action is successful, the promise should be resolved;
   * if the action is unsuccessful, the promise should be rejected.
   */
  action: (bodyProps: P, dialogState: S) => Promise<TResult> | ng.IPromise<TResult>;

  /**
   * if provided it will check this function to see if the action button should be disabled
   */
  actionDisabled?: (bodyProps: P, dialogState: S) => boolean;

  /**
   * If true display the action button as ButtonType.Destructive
   * False by default.
   */
  isActionDelete?: boolean;

  /**
   * if true the confirm-action buttons are centered in the dialog
   * False by default
   */
  centerActionButtons?: boolean;

    /**
   * Optionally specify the type of dialog to create
   */
  dialogType?: DialogType;

  /**
   * Optionally pass in style to the dialog body
   */
  dialogBodyStyle?: React.CSSProperties;

};

/**
 * Props for the client-specified dialog body content. The body is a controlled component, with the
 * dialog data being managed by its parent, so that this data can be passed into the action when the user clicks the button.
 */
export type ConfirmActionDialogBodyProps<P extends Object, S> = P & {
  readonly dialogState: S;
  readonly updateDialogState: (newState: React.SetStateAction<S>) => void;
};

/**
 * Opens a "confirm action" dialog. This is the most common dialog pattern, in which the user is presented with a
 * "Confirm" and "Cancel" button. The dialog also allows client-specified dialog content that lets the user
 * set up some data (the "dialog state") before confirming.
 *
 * Upon the user clicking "Confirm", the dialog will block (buttons disabled, dialog remains onscreen)
 * and the action will be performed. If the action is successful, the dialog then closes.
 * If the action is unsuccessful, the dialog will become unblocked but remain open, allowing the user to decide
 * to either try again or dismiss the dialog.
 *
 * This function returns a `ModalInstance` that wraps the entire workflow in a promise:
 * - After successful completion of the action, the dialog closes and the ModalInstance promise is resolved.
 * - If the dialog gets dismissed (via clicking the close button, glass, or cancel button), the promise gets rejected.
 *   Dismissing the dialog is a normal occurrence, so the calling code should swallow the rejection in most scenarios.
 */
export function openConfirmActionDialog<P extends Object, S, TResult>(options: ReactConfirmActionDialogOptions<P, S, TResult>): ModalInstance {
  let modalInstance: ModalInstance;

  function closeDialog(result?: unknown) {
    modalInstance.close(result);
  }

  function dismissDialog(reason?: unknown) {
    modalInstance.dismiss(reason);
  }

  const modalOptions: ReactModalOptions = {
    testId: ConfirmActionDialogTestId,
    title: options.title,
    type: (options.title && options.title.length > 0) ? DialogType.NormalWithTitleBar : DialogType.Uber,
    showCloseButton: true,
    dismissDialogOnCloseAction: true,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: DIALOG_WIDTH,
    dialogBodyStyle: options.dialogBodyStyle? options.dialogBodyStyle : dialogBodyStyle,
    disableAutoFocus: false,
    dialogContent: <ConfirmActionDialog {...options} closeDialog={closeDialog} dismissDialog={dismissDialog} />,
  };

  // Must set modalInstance variable so the close/dismiss functions have access to it in the closures
  modalInstance = ReactModalService.open(modalOptions);
  return modalInstance;
}

type ConfirmActionDialogProps<P extends Object, S, TResult> = ReactConfirmActionDialogOptions<P, S, TResult> & {
  closeDialog: ModalInstance['close'];
  dismissDialog: ModalInstance['dismiss'];
};

function ConfirmActionDialog<P extends Object, S, TResult>(props: ConfirmActionDialogProps<P, S, TResult>): JSX.Element {
  const bodyProps = props.initialBodyProps;
  const [dialogState, setDialogState] = React.useState<S>(props.initialDialogState);
  const [busy, setBusy] = React.useState<boolean>(false);

  const doAction = React.useCallback(async () => {
    setBusy(true);
    try {
      const result = await props.action(bodyProps, dialogState);
      await delayPromise(100); // Delay a short amount so that any action toast will appear before the dialog closes. It looks better this way.
      props.closeDialog(result);
    } catch {
      setBusy(false);
    }
  }, [setBusy, props.action, props.closeDialog, bodyProps, dialogState]);

  const actionName = (typeof props.actionName === 'string') ? props.actionName : props.actionName(bodyProps, dialogState);

  return (
    <>
      <props.bodyComponent {...bodyProps} dialogState={dialogState} updateDialogState={setDialogState} />
      <div className={props.centerActionButtons ? ActionButtonsCenter : ActionButtons} >
        <ButtonWithSpinner
          buttonType={ButtonType.Outline}
          clicked={() => props.dismissDialog('cancel')}
          testId={ConfirmActionDialogCancelButtonTestId}
          title={Messages.cancel()}
          disabled={busy}
        />
        <span className="tb-margin-left-wide">
          <ButtonWithSpinner
            buttonType={props.isActionDelete ? ButtonType.Destructive : ButtonType.Go}
            clicked={doAction}
            testId={ConfirmActionDialogConfirmButtonTestId}
            title={actionName}
            disabled={busy || (!_.isUndefined(props.actionDisabled) && props.actionDisabled(bodyProps, dialogState))}
            showSpinner={busy}
          />
        </span>
      </div>
    </>
  );
}

const actionButtonStyle: React.CSSProperties = {
  marginTop: TabStyles.Spacing.MediumSpace,
  whiteSpace: 'nowrap', // Prevent buttons from wrapping
  textAlign: 'right'
};

const ActionButtons = cssClass('confirm-action-dialog', 'action-buttons', actionButtonStyle);
const ActionButtonsCenter = cssClass('confirm-action-dialog', 'action-buttons-center', {
 ...actionButtonStyle,
 textAlign: 'center'
});

export const ConfirmActionDialogTestId = 'react-confirm-action-dialog';
export const ConfirmActionDialogCancelButtonTestId = 'confirm-action-dialog-cancel';
export const ConfirmActionDialogConfirmButtonTestId = 'confirm-action-dialog-confirm';

export const ReactConfirmActionDialogModule = {
  openConfirmActionDialog,
};
